import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, LogIn, LogOut, Download, Trash2, MailPlus, ShieldCheck, FileSignature, Activity, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { AdminAPI } from '../../api';

const META = {
  'auth.login': { icon: LogIn, color: 'text-[#0AA24F]', text: 'signed in' },
  'auth.logout': { icon: LogOut, color: 'text-[#6F76A7]', text: 'signed out' },
  'onboarding.submitted': { icon: FileSignature, color: 'text-[#0F766E]', text: 'signed an agreement' },
  'onboarding.pdf_downloaded': { icon: Download, color: 'text-[#2D8DF0]', text: 'opened an agreement PDF' },
  'onboarding.csv_exported': { icon: FileSpreadsheet, color: 'text-[#2D8DF0]', text: 'exported the agreements CSV' },
  'onboarding.deleted': { icon: Trash2, color: 'text-[#E5387B]', text: 'deleted an agreement' },
  'onboarding.reminder_sent': { icon: MailPlus, color: 'text-[#B7791F]', text: 'sent a reminder' },
  'admin.added': { icon: ShieldCheck, color: 'text-[#0F766E]', text: 'added an admin' },
  'admin.removed': { icon: ShieldCheck, color: 'text-[#E5387B]', text: 'removed an admin' },
};

const FILTERS = [
  { key: '', label: 'Everything' },
  { key: 'onboarding', label: 'Agreements' },
  { key: 'auth', label: 'Logins' },
  { key: 'admin', label: 'Admin changes' },
];

const detail = (e) => {
  const m = e.meta || {};
  if (e.action === 'onboarding.pdf_downloaded') return `${m.signer || e.object_id} · via ${m.via === 'admin' ? 'admin inbox' : 'secure email link'}`;
  if (e.action === 'onboarding.csv_exported') return `${m.count ?? 0} rows${m.q ? ` · search "${m.q}"` : ''}`;
  if (e.action === 'onboarding.deleted') return e.object_id;
  if (e.action === 'onboarding.submitted' || e.action === 'onboarding.reminder_sent') return m.email || e.object_id;
  if (e.action === 'admin.added' || e.action === 'admin.removed') return e.object_id;
  if (e.action === 'auth.login') return m.method === 'password' ? 'password login' : 'Google login';
  return e.object_id || '';
};

export const ActivityLog = () => {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState('');
  const load = useCallback((f = filter) => AdminAPI.activity(f ? { action: f } : {}).then(setEvents).catch(() => { setEvents([]); toast.error('Failed to load activity'); }), [filter]);
  useEffect(() => { load(filter); }, [filter, load]);

  return (
    <div data-testid="admin-activity">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="flex items-center gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key || 'all'} onClick={() => setFilter(f.key)} data-testid={`activity-filter-${f.key || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-[13px] font-medium ${filter === f.key ? 'bg-[#0A1551] text-white' : 'bg-white border border-[#D5D7ED] text-[#0A1551] hover:bg-[#F5F6FB]'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => load(filter)} className="sm:ml-auto inline-flex items-center gap-1.5 text-[13px] text-[#0A1551] hover:underline" data-testid="activity-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      {events === null ? <div className="text-[13px] text-[#6F76A7]">Loading…</div> : events.length === 0 ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-8 text-center" data-testid="activity-empty">
          <Activity className="w-8 h-8 mx-auto text-[#B0B4D0] mb-2" />
          <p className="text-[14px] font-semibold text-[#0A1551]">No activity recorded yet</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E3E5F5] rounded-xl divide-y divide-[#F0F1FB]">
          {events.map((e) => {
            const m = META[e.action] || { icon: Activity, color: 'text-[#6F76A7]', text: e.action };
            const Icon = m.icon;
            return (
              <div key={e.id} className="flex items-start gap-3 px-4 py-3" data-testid={`activity-row-${e.id}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${m.color}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-[#0A1551]">
                    <span className="font-semibold">{e.actor_name || e.actor_email || 'Public visitor'}</span> {m.text}
                  </div>
                  <div className="text-[12px] text-[#6F76A7] truncate">{detail(e)}</div>
                </div>
                <div className="text-[12px] text-[#9599C0] whitespace-nowrap shrink-0">{new Date(e.created_at).toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
